import { Components, Theme } from '@mui/material/styles';
import { darkPalette, lightPalette, ThemePalette } from './palette';

declare module '@mui/material/styles' {
  interface TypeBackground {
    glass: string;
    elevated: string;
    gradient: {
      primary: string;
      secondary: string;
      accent: string;
      hero: string;
      card: string;
    };
  }

  interface TypeText {
    blue: string;
    navy: string;
    teal: string;
    green: string;
    amber: string;
  }

  interface Palette {
    accent1: Palette['primary'];
    accent2: Palette['primary'];
    accent3: Palette['primary'];
    blue: Palette['primary'];
    navy: Palette['primary'];
    border: {
      light: string;
      medium: string;
      focus: string;
    };
  }
  interface PaletteOptions {
    accent1?: PaletteOptions['primary'];
    accent2?: PaletteOptions['primary'];
    accent3?: PaletteOptions['primary'];
    blue?: PaletteOptions['primary'];
    navy?: PaletteOptions['primary'];
    border?: {
      light: string;
      medium: string;
      focus: string;
    };
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent1: true;
    accent2: true;
    accent3: true;
  }
}

// Get theme components based on mode
export const getThemeComponents = (mode: 'light' | 'dark', palette: ThemePalette): Components<Omit<Theme, 'components'>> => {
  const isDark = mode === 'dark';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          scrollbarColor: `${palette.primary.main} ${palette.background.paper}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: palette.background.paper,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            background: palette.primary.dark,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            background: palette.primary.main,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 500,
          fontFamily: '"Poppins", sans-serif',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
          boxShadow: isDark ? '0 4px 14px rgba(30, 64, 175, 0.35)' : '0 4px 14px rgba(30, 64, 175, 0.25)',
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 100%)`,
            boxShadow: isDark ? '0 6px 20px rgba(30, 64, 175, 0.45)' : '0 6px 20px rgba(30, 64, 175, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${palette.secondary.main} 0%, ${palette.secondary.light} 100%)`,
          boxShadow: isDark ? '0 4px 14px rgba(14, 165, 233, 0.35)' : '0 4px 14px rgba(14, 165, 233, 0.25)',
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.secondary.dark} 0%, ${palette.secondary.main} 100%)`,
            boxShadow: isDark ? '0 6px 20px rgba(14, 165, 233, 0.45)' : '0 6px 20px rgba(14, 165, 233, 0.35)',
          },
        },
        outlined: {
          borderColor: palette.border.medium,
          color: palette.text.primary,
          '&:hover': {
            borderColor: palette.primary.light,
            background: isDark ? 'rgba(30, 64, 175, 0.1)' : 'rgba(30, 64, 175, 0.05)',
          },
        },
        text: {
          color: palette.text.primary,
          '&:hover': {
            background: isDark ? 'rgba(30, 64, 175, 0.1)' : 'rgba(30, 64, 175, 0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: palette.background.glass,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${palette.border.light}`,
          borderRadius: 16,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: palette.border.medium,
            boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: palette.background.paper,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: isDark ? 'rgba(11, 17, 32, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${palette.border.light}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        filled: {
          background: isDark ? 'rgba(30, 64, 175, 0.2)' : 'rgba(30, 64, 175, 0.1)',
          border: `1px solid ${isDark ? 'rgba(30, 64, 175, 0.3)' : 'rgba(30, 64, 175, 0.2)'}`,
        },
        outlined: {
          borderColor: palette.border.medium,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: palette.background.glass,
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${palette.border.light}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            '& fieldset': {
              borderColor: palette.border.medium,
            },
            '&:hover fieldset': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary.main,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.background.elevated,
          border: `1px solid ${palette.border.light}`,
          borderRadius: 8,
          fontFamily: '"Poppins", sans-serif',
          fontSize: '0.75rem',
          color: palette.text.primary,
        },
        arrow: {
          color: palette.background.elevated,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: palette.background.glass,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${palette.border.light}`,
          borderRadius: 16,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: palette.background.glass,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${palette.border.light}`,
          borderRadius: 12,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: palette.primary.main,
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontFamily: '"Poppins", sans-serif',
          '&.Mui-selected': {
            color: palette.primary.light,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.divider,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.primary.main,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(30, 64, 175, 0.15)' : 'rgba(30, 64, 175, 0.1)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: palette.primary.main,
                opacity: 1,
                border: 0,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
          },
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: isDark ? '#39393D' : '#E9E9EA',
            opacity: 1,
          },
        },
      },
    },
  };
};

// Inject theme-specific CSS styles
export const injectThemeStyles = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';
  const palette = isDark ? darkPalette : lightPalette;

  // Remove existing style element if present
  const existingStyle = document.getElementById('sebear-theme-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  const style = document.createElement('style');
  style.id = 'sebear-theme-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

    :root {
      --bg-default: ${palette.background.default};
      --bg-paper: ${palette.background.paper};
      --bg-glass: ${palette.background.glass};
      --text-primary: ${palette.text.primary};
      --text-secondary: ${palette.text.secondary};
      --border-light: ${palette.border.light};
      --border-medium: ${palette.border.medium};
      --primary-main: ${palette.primary.main};
      --primary-light: ${palette.primary.light};
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.8;
        transform: scale(1.02);
      }
    }

    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }

    @keyframes gradientFlow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .fade-in-up {
      animation: fadeInUp 0.5s ease-out;
    }

    .fade-in {
      animation: fadeIn 0.4s ease-out;
    }

    .slide-in-right {
      animation: slideInRight 0.4s ease-out;
    }

    .shimmer {
      background: linear-gradient(
        90deg,
        ${isDark ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0)'} 0%,
        ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'} 50%,
        ${isDark ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0)'} 100%
      );
      background-size: 200% 100%;
      animation: shimmer 2s infinite;
    }

    .gradient-text {
      background: linear-gradient(135deg, #3B82F6, #0EA5E9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .gradient-text-accent {
      background: linear-gradient(135deg, #10B981, #0EA5E9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .gradient-border {
      position: relative;
      background: ${palette.background.gradient.card};
      border-radius: 16px;
    }

    .gradient-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 16px;
      padding: 1px;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(14, 165, 233, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }

    .glass-card {
      background: ${palette.background.glass};
      backdrop-filter: blur(20px);
      border: 1px solid ${palette.border.light};
      border-radius: 16px;
    }

    .hover-lift {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .hover-lift:hover {
      transform: translateY(-4px);
      box-shadow: ${isDark ? '0 12px 40px rgba(0, 0, 0, 0.3)' : '0 12px 40px rgba(0, 0, 0, 0.15)'};
    }

    /* Professional status colors */
    .status-success {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.1));
      border: 1px solid rgba(16, 185, 129, 0.3);
      color: ${isDark ? '#34D399' : '#059669'};
    }

    .status-warning {
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.1));
      border: 1px solid rgba(245, 158, 11, 0.3);
      color: ${isDark ? '#FBBF24' : '#D97706'};
    }

    .status-error {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1));
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: ${isDark ? '#F87171' : '#DC2626'};
    }

    .status-info {
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(14, 165, 233, 0.1));
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: ${isDark ? '#38BDF8' : '#0284C7'};
    }

    /* Data grid styling */
    .data-row {
      transition: background-color 0.15s ease;
    }

    .data-row:hover {
      background-color: ${isDark ? 'rgba(30, 64, 175, 0.1)' : 'rgba(30, 64, 175, 0.05)'};
    }

    /* Professional number styling */
    .currency {
      font-family: 'Poppins', sans-serif;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    .percentage {
      font-family: 'Poppins', sans-serif;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }

    /* Mobile responsiveness */
    @media (max-width: 768px) {
      .hide-mobile {
        display: none !important;
      }

      .mobile-full-width {
        width: 100% !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// Typography configuration (shared between modes)
export const typography = {
  fontFamily: '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.2,
    fontFamily: '"Poppins", sans-serif',
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.25rem',
    fontWeight: 600,
    lineHeight: 1.3,
    fontFamily: '"Poppins", sans-serif',
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '1.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: '"Poppins", sans-serif',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: '"Poppins", sans-serif',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: '"Poppins", sans-serif',
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: '"Poppins", sans-serif',
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    fontWeight: 400,
    fontFamily: '"Poppins", sans-serif',
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    fontWeight: 400,
    fontFamily: '"Poppins", sans-serif',
  },
  button: {
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif',
    textTransform: 'none' as const,
  },
  caption: {
    fontWeight: 400,
    fontFamily: '"Poppins", sans-serif',
    fontSize: '0.75rem',
  },
  overline: {
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif',
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
  },
};

export const shape = {
  borderRadius: 12,
};
